import { GAMEPLAY_UPLOAD_STEPS } from "@/store/coaching/gameplay-analysis/gameplay-analysis.constants"
import { LOCAL_STORAGE_GPA_CHECKOUT_KEYS } from "@/store/vod/vod.constants"
import {
  getVodPrice,
  createVodRequest,
  uploadVideo,
  getStudentRequests,
  flagFeedbackRecommendation,
  getVodAvailableGames,
  rateVodRequestAsStudent,
  retrievePreSignedUploadUrlByStudent,
  getAvailableLanguagesByGame,
  getAvailableCoachesByGame,
} from "../../../api/vod"
import { getSessionRequest } from "../../../api/user/coaching/student/sessionRequests"
import { fetchChampions } from "../../../api/leagueoflegends"
import { fetchCharacters } from "../../../api/ssbu"
import { getRecommendedCoaches } from "../../../api/coaching"
import Coach from "./vod/coach"

const state = {
  vodId: "",
  videoError: "",
  detailsError: "",
  videoToUpload: null,
  usingReplayCode: false,
  replayCode: "",
  videoUrl: "",
  videoMeta: null,
  game: null,
  availableGames: [],
  comments: [],
  vodPrice: 0,
  uploadingProgress: 0,
  cancelUploadingFunction: null,
  studentRequests: [],
  sessionRequestById: null,
  showRefill: false,
  recordHintsForGame: null,
  availableChampions: [],
  playerName: "",
  champion: null,
  requestSubmitting: false,
  requestRated: false,
  recommendedCoaches: [],
  availableLanguages: [],
  language: "English",
  availableCoaches: [],
  coach: null,
  replayMeta: null,
  isRestoringCheckoutState: false,
}
const getters = {
  videoError: state => state.videoError,
  comments: state => state.comments,
  game: state => state.game,
  detailsError: state => state.detailsError,
  videoUrl: state => state.videoUrl,
  replayCode: state => state.replayCode,
  vodPrice: state => state.vodPrice,
  vodId: state => state.vodId,
  videoToUpload: state => state.videoToUpload,
  uploadingProgress: state => state.uploadingProgress,
  cancelUploadingFunction: state => state.cancelUploadingFunction,
  studentRequests: state => state.studentRequests,
  sessionRequestById: state => state.sessionRequestById,
  videoMeta: state => state.videoMeta,
  showRefill: state => state.showRefill,
  availableGames: state => state.availableGames,
  recordHintsForGame: state => state.recordHintsForGame,
  availableChampions: state => state.availableChampions,
  playerName: state => state.playerName,
  champion: state => state.champion,
  requestSubmitting: state => state.requestSubmitting,
  requestRated: state => state.requestRated,
  recommendedCoaches: state => state.recommendedCoaches,
  usingReplayCode: state => state.usingReplayCode,
  availableLanguages: state => state.availableLanguages,
  language: state => state.language,
  availableCoaches: state => state.availableCoaches,
  coach: state => state.coach,
  // Synced state from general coaching service order flow
  selectedPaymentMethod: (state, getters, rootState, rootGetters) => {
    return rootGetters["Coaching/CoachService/selectedPaymentMethod"]
  },
  replayMeta: state => state.replayMeta,
  isGameplayUploadReady: state => {
    return !!((state.usingReplayCode && state.replayCode) || state.videoUrl)
  },
  isRestoringCheckoutState: state => state.isRestoringCheckoutState,
}
const mutations = {
  setVideoUrl: (state, videoUrl) => (state.videoUrl = videoUrl),
  setReplayCode: (state, replayCode) => (state.replayCode = replayCode),
  setVideoError: (state, error) => (state.videoError = error),
  setComments: (state, comments) => (state.comments = comments),
  setGame: (state, game) => (state.game = game),
  setAvailableGames: (state, games) => (state.availableGames = games),
  setVodPrice: (state, price) => (state.vodPrice = price),
  setVodId: (state, vodId) => (state.vodId = vodId),
  setVideoToUpload: (state, video) => (state.videoToUpload = video),
  setUploadingProgress: (state, progress) =>
    (state.uploadingProgress = progress),
  setCancelUploadingFunction: (state, cancelFunction) =>
    (state.cancelUploadingFunction = cancelFunction),
  setStudentRequests: (state, requests) => (state.studentRequests = requests),
  setSessionRequestById: (state, request) =>
    (state.sessionRequestById = request),
  setVideoMeta: (state, videoMeta) => (state.videoMeta = videoMeta),
  setShowRefill: (state, bol) => (state.showRefill = bol),
  setRecordHintsForGame: (state, recordHintsForGame) =>
    (state.recordHintsForGame = recordHintsForGame),
  setAvailableChampions: (state, availableChampions) =>
    (state.availableChampions = availableChampions),
  setPlayerName: (state, playerName) => (state.playerName = playerName),
  setChampion: (state, champion) => (state.champion = champion),
  setDetailsError: (state, detailsError) => (state.detailsError = detailsError),
  setRequestSubmitting: (state, requestSubmitting) =>
    (state.requestSubmitting = requestSubmitting),
  setRequestRated: (state, requestRated) => (state.requestRated = requestRated),
  setRecommendedCoaches: (state, coaches) =>
    (state.recommendedCoaches = coaches),
  setUsingReplayCode: (state, usingReplayCode) =>
    (state.usingReplayCode = usingReplayCode),
  setAvailableLanguages: (state, languages) =>
    (state.availableLanguages = languages),
  setLanguage: (state, language) => (state.language = language),
  setAvailableCoaches: (state, coaches) => (state.availableCoaches = coaches),
  setCoach: (state, coach) => (state.coach = coach),
  setReplayMeta: (state, meta) => (state.replayMeta = meta),
  SET_CHECKOUT_RESTORE_STATE: (state, isRestoring) => (state.isRestoringCheckoutState = isRestoring),
}
const actions = {
  async addComment ({ commit, state }, comment) {
    const comments = [...state.comments, comment]
    comments.sort((a, b) => a.at - b.at)
    await commit("setComments", comments)
  },
  async removeComment ({ commit, state }, removingComment) {
    const comments = state.comments.filter(
      comment => comment.at !== removingComment.at
    )
    await commit("setComments", comments)
  },
  async loadVodPrice ({ commit }) {
    const data = await getVodPrice()
    await commit("setVodPrice", data.result)
  },
  async loadRecommendedCoaches ({ commit }, { game, languages }) {
    const data = await getRecommendedCoaches({ game, languages })
    const coaches = data.result.data.sort(
      (a, b) =>
        b.coach.simple_data.requests.avg - a.coach.simple_data.requests.avg
    )
    const recommendedCoaches = []
    let i
    let j = 0
    for (i = 0; i < coaches.length; i++) {
      if (recommendedCoaches.length >= 6) {
        break
      }
      if (coaches[i].coach.status !== "offline") {
        recommendedCoaches.push(coaches[i])
      }
    }
    while (recommendedCoaches.length < 6 && j < coaches.length) {
      if (i < coaches.length) {
        recommendedCoaches.push(coaches[i])
        i++
      } else if (j < coaches.length && coaches[j].coach.status === "offline") {
        recommendedCoaches.push(coaches[j])
        j++
      } else {
        j++
      }
    }
    await commit("setRecommendedCoaches", recommendedCoaches)
  },
  async createVodRequest ({ state, commit, dispatch, getters, rootGetters }, getInstaProAccessToken) {
    let champion = null
    if (state.champion) {
      champion = {
        name: state.champion.name,
        thumbnail_image: state.champion.thumbnail_image,
      }
    }
    let attachments
    if (state.usingReplayCode) {
      attachments = [
        {
          replay_code: state.replayCode,
          comments: state.comments,
          metadata: state.replayMeta,
        },
      ]
    } else {
      attachments = [
        {
          url: state.videoUrl,
          comments: state.comments,
          metadata: state.videoMeta,
        },
      ]
    }

    let coachId
    if (state.coach) {
      coachId = state.coach.user_id
    }

    const offerAmount =
      rootGetters["Coaching/GameplayAnalysis/offerAmount"] || null
    const body = {
      game_id: state.game.id,
      type: "async-vod-review",
      champion,
      language: state.language,
      in_game_username: state.playerName,
      attachments,
      payment_method: getters.selectedPaymentMethod,
      offered_amount: offerAmount ? offerAmount * 100 : null, // Convert to cent value (for assumed USD currency)
      coach_id: coachId,
    }
    try {
      if (state.requestSubmitting) {
        return
      }
      await commit("setRequestSubmitting", true)
      const data = await createVodRequest(body, getInstaProAccessToken)
      await commit("setVodId", data.request_id)
      await dispatch("clearUploadedRequestData")
      commit("setRequestSubmitting", false)
      await dispatch("Coaching/GameplayAnalysis/setGameplayUploadStep", GAMEPLAY_UPLOAD_STEPS.REQUEST_RECEIVED, { root: true })
    } catch (error) {
      commit("setRequestSubmitting", false)
    }
  },
  async uploadVideoByStudent ({ state, commit, dispatch, rootGetters }) {
    const instaProAccessToken =
      rootGetters["User/Coaching/getInstaProAccessToken"]
    const fileNameComponents = state.videoToUpload.name.split(".")
    const fileExtension = fileNameComponents[fileNameComponents.length - 1]
    const urls = await retrievePreSignedUploadUrlByStudent(
      instaProAccessToken,
      state.game.id,
      fileExtension
    )
    const data = await uploadVideo(urls.data.upload_url, state.videoToUpload)
    commit("setUploadingProgress", 0)
    if (data.message) {
      commit("setVideoError", data.message)
      return false
    }
    if (data.toString() === "Cancel") {
      return false
    }
    commit("setVideoUrl", urls.data.asset_url)
    await dispatch("Coaching/GameplayAnalysis/setGameplayUploadStep", GAMEPLAY_UPLOAD_STEPS.ENTER_DETAILS, { root: true })
    return true
  },
  async loadStudentRequests ({ commit, rootGetters }) {
    try {
      const instaProAccessToken =
        rootGetters["User/Coaching/getInstaProAccessToken"]
      const data = await getStudentRequests(instaProAccessToken)
      commit("setStudentRequests", data.result.data)
    } catch (error) {
      console.error(error)
    }
  },
  async loadSessionRequestById ({ commit, rootGetters }, requestId) {
    try {
      const instaProAccessToken =
        rootGetters["User/Coaching/getInstaProAccessToken"]
      const data = await getSessionRequest(instaProAccessToken, requestId)
      commit("setSessionRequestById", data.result)
    } catch (error) {
      console.error(error)
    }
  },
  async flagFeedbackRecommendation (
    { rootGetters },
    { recommendationId, complete }
  ) {
    try {
      const instaProAccessToken =
        rootGetters["User/Coaching/getInstaProAccessToken"]
      const body = {
        completed_by_student: complete,
      }
      await flagFeedbackRecommendation(
        recommendationId,
        body,
        instaProAccessToken
      )
    } catch (error) {
      console.error(error)
    }
  },
  clearUploadedRequestData ({ commit }) {
    for (const localStorageKey in LOCAL_STORAGE_GPA_CHECKOUT_KEYS) {
      window.localStorage.removeItem(localStorageKey)
    }
    commit("setUsingReplayCode", false)
    commit("setReplayCode", "")
    commit("setReplayMeta", null)
    commit("setVideoUrl", "")
    commit("setVideoMeta", null)
    commit("setComments", [])
    commit("setChampion", null)
    commit("setPlayerName", "")
    commit("setCoach", null)
  },
  async fetchAvailableVodGames ({ commit }) {
    const data = await getVodAvailableGames()
    commit("setAvailableGames", data.result)
  },
  setGame ({ commit }, game) {
    commit("setGame", game)
  },
  async fetchChampions ({ commit, state }) {
    let data = []
    if (state.game.name === "leagueoflegends") {
      const champions = await fetchChampions()
      data = Object.values(champions)
    } else if (state.game.name === "super-smash-bros-ultimate") {
      const characters = await fetchCharacters()
      data = Object.values(characters)
    }
    commit("setAvailableChampions", data)
  },
  async rateVodRequestAsStudent ({ commit, state, rootGetters }, body) {
    const requestId = state.sessionRequestById.id
    const instaProAccessToken =
      rootGetters["User/Coaching/getInstaProAccessToken"]
    await rateVodRequestAsStudent(requestId, body, instaProAccessToken)
    commit("setRequestRated", true)
  },
  async getAvailableLanguagesByGame ({ commit, state }) {
    const gameId = state.game.id
    const data = await getAvailableLanguagesByGame(gameId)
    commit("setAvailableLanguages", data.result)
  },
  async getAvailableCoachesByGame ({ commit, state }) {
    const gameName = state.game.name
    const data = await getAvailableCoachesByGame(gameName)
    commit("setAvailableCoaches", data.result.data)
  },
  setCheckoutRestoreState ({ commit }, isRestoring) {
    commit("SET_CHECKOUT_RESTORE_STATE", isRestoring)
  },
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
  modules: {
    Coach,
  },
}



// WEBPACK FOOTER //
// ./src/store/modules/instapro/vod.js